import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  FormControlLabel,
  Switch,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { ApplicationPackage, ApplicationPackageRequest } from "../../hooks/useApplicationPackages";

interface PackageFormModalProps {
  open: boolean;
  handleClose: () => void;
  onSubmit: (formData: ApplicationPackageRequest & { id?: number }) => void;
  isSubmitting?: boolean;
  isEdit?: boolean;
  initialData?: ApplicationPackage;
}

const PackageFormModal: React.FC<PackageFormModalProps> = ({
  open,
  handleClose,
  onSubmit,
  isSubmitting = false,
  isEdit = false,
  initialData,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<ApplicationPackageRequest & { id?: number }>({
    name: "",
    description: "",
    active: true,
    price: 0,
  });

  useEffect(() => {
    if (open) {
      if (isEdit && initialData) {
        setFormData({
          id: initialData.id,
          name: initialData.name || "",
          description: initialData.description || "",
          active: initialData.active ?? true,
          price: initialData.price ?? 0,
        });
      } else {
        setFormData({ name: "", description: "", active: true, price: 0 });
      }
    }
  }, [open, isEdit, initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...(isEdit && formData.id != null ? { id: formData.id } : {}),
      name: formData.name.trim(),
      description: formData.description?.trim() || "",
      active: formData.active ?? true,
      price: Number(formData.price) || 0,
    });
  };

  const isValid = !!formData.name.trim();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#f8f9fb",
          borderBottom: "2px solid #e0e7ff",
        }}
      >
        {isEdit ? t("edit_package") : t("add_package")}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ mt: 1 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label={t("package_name")}
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label={t("description")}
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label={t("price")}
              type="number"
              value={formData.price ?? ""}
              onChange={(e) => setFormData((prev) => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
              fullWidth
              inputProps={{ min: 0, step: "0.01" }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.active ?? true}
                  onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.checked }))}
                />
              }
              label={t("active")}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
          <Button onClick={handleClose} color="inherit" disabled={isSubmitting}>
            {t("cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isValid || isSubmitting}
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
          >
            {isSubmitting ? t("saving") : isEdit ? t("update") : t("add")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PackageFormModal;
